import { useCallback, useEffect, useRef, useState } from 'react';

// Keep the already-shipped homepage key and OS-following default across real routes.
export const THEME_KEY = 'henosis.homepage.theme';
export const SIDEBAR_KEY = 'henosis.ui.sidebar';
const CHANGE_EVENT = 'henosis:ui-preference';
const memoryFallback = new Map();
const themeChoices = ['system', 'light', 'dark'];
const parse = (key, value, fallback) => key === THEME_KEY
  ? (themeChoices.includes(value) ? value : fallback)
  : (value === 'true' ? true : value === 'false' ? false : fallback);

export function useUIPreference(name, fallback) {
  const key = name === 'theme' ? THEME_KEY : SIDEBAR_KEY;
  const read = useCallback(() => {
    if (memoryFallback.has(key)) return memoryFallback.get(key);
    try { return parse(key, window.localStorage.getItem(key), fallback); }
    catch { return fallback; }
  }, [key, fallback]);
  const [value, setValue] = useState(read);
  const current = useRef(value);
  current.current = value;
  useEffect(() => {
    const sync = (event) => {
      if (event.detail?.key !== key) return;
      current.current = event.detail.value;
      setValue(event.detail.value);
    };
    const storage = (event) => {
      if (event.key !== key && event.key !== null) return;
      memoryFallback.delete(key);
      const next = parse(key, event.key === null ? null : event.newValue, fallback);
      current.current = next;
      setValue(next);
    };
    window.addEventListener('storage', storage);
    window.addEventListener(CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener('storage', storage);
      window.removeEventListener(CHANGE_EVENT, sync);
    };
  }, [key, fallback]);
  const update = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(current.current) : next;
    if (key === THEME_KEY && !themeChoices.includes(resolved)) return;
    try {
      window.localStorage.setItem(key, String(resolved));
      memoryFallback.delete(key);
    } catch { memoryFallback.set(key, resolved); }
    current.current = resolved;
    setValue(resolved);
    // Never re-read failed storage here: an explicit per-visit choice must remain usable.
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { key, value: resolved } }));
  }, [key]);
  return [value, update];
}

export function useUITheme() {
  const [choice, change] = useUIPreference('theme', 'system');
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)')?.matches || false);
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onChange = (event) => setSystemDark(event.matches);
    media?.addEventListener('change', onChange);
    return () => media?.removeEventListener('change', onChange);
  }, []);
  return { choice, change, resolved: choice === 'system' ? (systemDark ? 'dark' : 'light') : choice };
}
